import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { api } from "@/lib/api";
import { formatBRL } from "@/lib/format";
import { getProductImageUrl } from "@/lib/utils";
import { Loader2, ArrowRight, User, MapPin } from "lucide-react";
import { toast } from "sonner";

const Input = ({ label, name, value, onChange, type = "text", required = true, ...rest }) => (
  <label className="flex flex-col gap-2">
    <span className="text-xs font-quicksand font-bold text-[#3d3d3d]">{label}{required && " *"}</span>
    <input type={type} name={name} value={value} onChange={onChange} required={required} data-testid={`input-${name}`} className="input-gp" {...rest} />
  </label>
);

export default function Checkout() {
  const { items, total, clear } = useCart();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: "", email: "", phone: "", cpf_cnpj: "",
    address: "", city: "", state: "", zip_code: "",
  });
  const [selectedShipping, setSelectedShipping] = useState(null);
  const [shippingInfo, setShippingInfo] = useState(null);
  const [loadingShipping, setLoadingShipping] = useState(false);

  const onChange = (e) => {
    let { name, value } = e.target;
    if (name === "zip_code") {
      let val = value.replace(/\D/g, "");
      if (val.length > 5) {
        val = val.substring(0, 5) + "-" + val.substring(5, 8);
      }
      value = val;
    }
    setForm((f) => ({ ...f, [name]: value }));
  };

  React.useEffect(() => {
    const cleanCep = form.zip_code.replace(/\D/g, "");
    if (cleanCep.length === 8) {
      setLoadingShipping(true);
      api.post("/shipping/quote", {
        cep: cleanCep,
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity }))
      })
        .then((r) => {
          setShippingInfo(r.data);
          if (r.data.options && r.data.options.length > 0) {
            setSelectedShipping(r.data.options[0]);
          } else {
            setSelectedShipping(null);
          }
          setForm((f) => ({
            ...f,
            city: f.city || r.data.city,
            state: f.state || r.data.uf,
          }));
        })
        .catch(() => {
          setSelectedShipping(null);
          setShippingInfo(null);
        })
        .finally(() => {
          setLoadingShipping(false);
        });
    } else {
      setSelectedShipping(null);
      setShippingInfo(null);
    }
  }, [form.zip_code, items]);

  if (items.length === 0) {
    return (
      <div className="max-w-2xl mx-auto px-6 sm:px-8 py-24 text-center">
        <h1 className="font-quicksand text-3xl font-extrabold text-[#3d3d3d]">Carrinho vazio</h1>
        <p className="text-sm text-[#666] mt-3">Adicione produtos antes de finalizar a compra.</p>
        <button onClick={() => navigate("/loja")} data-testid="empty-cart-go-shop" className="btn-gp mt-8">
          IR AO CATÁLOGO
        </button>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    if (shippingInfo && !selectedShipping) {
      toast.error("Por favor, selecione uma opção de frete.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
        customer: { ...form },
        shipping_option: selectedShipping ? {
          id: selectedShipping.id,
          name: selectedShipping.name,
          cost: selectedShipping.cost
        } : null,
      };
      const { data } = await api.post("/orders", payload);
      clear();
      navigate(`/pedido/${data.id}`);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Erro ao criar pedido";
      toast.error(typeof msg === "string" ? msg : "Erro ao criar pedido");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-6 sm:px-8 py-12">
      <div className="text-center mb-10">
        <div className="text-xs uppercase tracking-widest font-quicksand font-bold text-[#1BB0CE] mb-3">
          CHECKOUT
        </div>
        <h1 className="font-quicksand text-4xl font-extrabold text-[#3d3d3d]">Finalizar pedido</h1>
        <p className="text-sm text-[#666] mt-3">Preencha seus dados para gerar o PIX</p>
      </div>

      <form onSubmit={submit} className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          <section className="card-soft p-6 sm:p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-full bg-gp-gradient-light flex items-center justify-center">
                <User className="w-5 h-5 text-[#1BB0CE]" />
              </div>
              <h2 className="font-quicksand text-xl font-extrabold text-[#3d3d3d]">Dados pessoais</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Input label="Nome completo" name="name" value={form.name} onChange={onChange} />
              <Input label="E-mail" name="email" type="email" value={form.email} onChange={onChange} />
              <Input label="Telefone (com DDD)" name="phone" value={form.phone} onChange={onChange} placeholder="11999990000" />
              <Input label="CPF / CNPJ" name="cpf_cnpj" value={form.cpf_cnpj} onChange={onChange} placeholder="000.000.000-00" />
            </div>
          </section>

          <section className="card-soft p-6 sm:p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-full bg-gp-gradient-light flex items-center justify-center">
                <MapPin className="w-5 h-5 text-[#1BB0CE]" />
              </div>
              <h2 className="font-quicksand text-xl font-extrabold text-[#3d3d3d]">Endereço de entrega</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Input label="Endereço" name="address" value={form.address} onChange={onChange} />
              <Input label="CEP" name="zip_code" value={form.zip_code} onChange={onChange} placeholder="00000-000" />
              <Input label="Cidade" name="city" value={form.city} onChange={onChange} />
              <Input label="Estado (UF)" name="state" value={form.state} onChange={onChange} maxLength={2} />
            </div>
          </section>

          {shippingInfo && (
            <section className="card-soft p-6 sm:p-8 animate-fade-in">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-full bg-gp-gradient-light flex items-center justify-center text-lg">
                  🚚
                </div>
                <h2 className="font-quicksand text-xl font-extrabold text-[#3d3d3d]">Forma de envio</h2>
              </div>
              <div className="space-y-3">
                {shippingInfo.options.map((opt) => {
                  const isSelected = selectedShipping?.id === opt.id;
                  return (
                    <label
                      key={opt.id}
                      className={`flex items-center justify-between p-4 border-2 rounded-2xl cursor-pointer transition-all ${
                        isSelected
                          ? "border-[#1BB0CE] bg-[#1BB0CE]/5"
                          : "border-[#e4e4e7] hover:border-[#3d3d3d]/20"
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <input
                          type="radio"
                          name="shipping_option"
                          checked={isSelected}
                          onChange={() => setSelectedShipping(opt)}
                          className="w-4 h-4 text-[#1BB0CE] border-[#e4e4e7] focus:ring-[#1BB0CE]"
                        />
                        <div className="font-quicksand text-sm">
                          <span className="font-bold text-[#3d3d3d] block">{opt.name}</span>
                          <span className="text-[#666] text-xs">
                            Prazo estimado: {opt.delivery_time} {opt.delivery_time === 1 ? "dia útil" : "dias úteis"}
                          </span>
                        </div>
                      </div>
                      <div className="font-quicksand font-bold text-sm text-[#1BB0CE]">
                        {opt.cost === 0 ? "Grátis" : formatBRL(opt.cost)}
                      </div>
                    </label>
                  );
                })}
              </div>
            </section>
          )}
        </div>

        <div className="lg:col-span-1">
          <div className="card-soft p-6 sticky top-32">
            <h2 className="font-quicksand text-xl font-extrabold text-[#3d3d3d] mb-5">Resumo</h2>
            <ul className="divide-y divide-[#f1f1f3]">
              {items.map((i) => (
                <li key={i.product_id} className="py-3 flex gap-3" data-testid={`summary-${i.product_id}`}>
                  <div className="w-12 h-12 bg-gp-gradient-light rounded-xl overflow-hidden">
                    <img src={getProductImageUrl(i.image_url)} alt="" className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1 text-xs min-w-0">
                    <div className="font-quicksand font-bold text-[#3d3d3d] truncate">{i.name}</div>
                    <div className="text-[#666]">{i.quantity}x {formatBRL(i.price)}</div>
                  </div>
                  <div className="text-xs font-quicksand font-bold text-[#3d3d3d]">{formatBRL(i.quantity * i.price)}</div>
                </li>
              ))}
            </ul>
            <div className="border-t border-[#f1f1f3] mt-4 pt-4 space-y-2">
              <div className="flex justify-between text-xs font-quicksand">
                <span className="text-[#666] font-bold">Subtotal</span>
                <span className="text-[#3d3d3d] font-bold">{formatBRL(total)}</span>
              </div>
              <div className="flex justify-between text-xs font-quicksand">
                <span className="text-[#666] font-bold">Frete</span>
                <span className="text-[#3d3d3d] font-bold">
                  {loadingShipping ? (
                    "Calculando..."
                  ) : selectedShipping ? (
                    selectedShipping.cost === 0 ? (
                      <span className="text-[#00D9A6]">Grátis</span>
                    ) : (
                      formatBRL(selectedShipping.cost)
                    )
                  ) : (
                    "Digite o CEP"
                  )}
                </span>
              </div>
              <div className="border-t border-[#f1f1f3] pt-3 flex justify-between items-baseline">
                <span className="text-xs uppercase tracking-wider font-quicksand font-bold text-[#666]">Total PIX</span>
                <span className="font-quicksand text-2xl font-extrabold text-gradient-gp" data-testid="checkout-total">
                  {formatBRL(total + (selectedShipping ? selectedShipping.cost : 0))}
                </span>
              </div>
            </div>
            <button type="submit" disabled={submitting} data-testid="confirm-order-button" className="btn-gp w-full mt-6">
              {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> GERANDO PIX...</> : <>GERAR PIX <ArrowRight className="w-4 h-4" /></>}
            </button>
            <p className="text-[10px] text-[#999] mt-4 text-center font-quicksand">
              Pagamento processado por Asaas
            </p>
          </div>
        </div>
      </form>
    </div>
  );
}
